<template>
	<div class="analytics-editor reports-typography">
		<header class="editor-header">
			<div>
				<h2>{{ headerTitle }}</h2>
			</div>
			<div class="header-actions">
				<DateRangePicker
					class="analytics-month-picker"
					use-fixed
					animated
					:dateFrom="form.dateFrom"
					:dateTo="form.dateTo"
					mode="month"
					:disabled="isLocked"
					placeholder="Select month"
					@update="handleMonthPickerUpdate"
				/>
			</div>
		</header>

		<p v-if="isRefreshingSnapshot" class="range-status">Refreshing data for selected range…</p>

		<section class="summary-grid">
			<div class="summary-card">
				<span class="summary-label">Invoiced revenue</span>
				<strong class="summary-value">{{ formatCurrency(summary.revenue) }}</strong>
			</div>
			<div class="summary-card">
				<span class="summary-label">Labour cost</span>
				<strong class="summary-value">{{ formatCurrency(summary.laborCost) }}</strong>
			</div>
			<div class="summary-card">
				<span class="summary-label">Purchases</span>
				<strong class="summary-value">{{ formatCurrency(summary.purchasesGross) }}</strong>
			</div>
			<div class="summary-card">
				<span class="summary-label">Gross margin</span>
				<strong class="summary-value" :class="{ negative: summary.grossMargin < 0 }">
					{{ formatCurrency(summary.grossMargin) }}
				</strong>
			</div>
			<div class="summary-card">
				<span class="summary-label">Margin %</span>
				<strong class="summary-value">{{ formatPercent(summary.marginPct) }}</strong>
			</div>
			<div class="summary-card">
				<span class="summary-label">Total hours</span>
				<strong class="summary-value">{{ formatNumber(summary.totalHours) }}</strong>
			</div>
      <div class="summary-card"><span class="summary-label">Paid to suppliers · by payment date</span><strong class="summary-value">{{ formatCurrency(summary.wagePaymentsTotal) }}</strong><small>Shown separately from labour cost</small></div>
      <div v-if="summary.invoicePaymentDifference" class="summary-card"><span class="summary-label">Paid invoices vs linked appointments</span><strong class="summary-value">{{ formatCurrency(summary.invoicePaymentDifference) }}</strong></div>
    </section>
    <p v-if="form.wagePayments.some((item) => item.linkagePending)" class="range-status">Some paid invoices still need their appointments reviewed. Their payments are included; their work costs remain estimates.</p>
    <section class="charts-stack">
			<div class="charts-overview-grid">
				<AnalyticsChartCard
					title="Cost structure"
					subtitle="Split of operating components for current report"
					:option="costStructureOption"
					:empty="!hasCostStructureData"
					empty-text="No cost structure data available."
					height="320px"
				/>
				<AnalyticsChartCard
					title="Building profitability"
					subtitle="Top buildings by revenue with cost and margin"
					:option="buildingProfitabilityOption"
					:empty="!hasBuildingProfitabilityData"
					empty-text="No building-level data in current snapshot."
					height="320px"
				/>
			</div>
		</section>

		<div class="snapshot-tabs-row">
			<SegmentedSwitch v-model="activeSnapshotTab" :options="snapshotTabOptions" label="Snapshot sections" />
		</div>

		<section v-if="activeSnapshotTab === 'appointments'" class="snapshot-section">
			<div class="snapshot-toolbar">
				<button class="payment-list-button" type="button" :disabled="isLocked" @click="handleAddAppointment">
					<AppointmentIcon name="plus" />
					Add row
				</button>
				<div class="group-by">
					<span class="group-by-label">Group by</span>
					<CatalogSearchInput
						class="group-by-input"
						:modelValue="groupBySelection"
						:options="groupByOptions"
						:multiple="true"
						placeholder="Select fields"
						@update:modelValue="updateGroupBySelection"
					/>
				</div>
			</div>
			<div class="table-wrapper">
				<table class="snapshot-table" :class="{ 'full-items-mode': isUngroupedAppointments }">
					<thead>
						<tr v-if="isUngroupedAppointments">
							<th class="col-date">Date</th>
							<th>Building</th>
							<th>Cleaner</th>
							<th>Service</th>
							<th class="col-hours numeric">Hours</th>
							<th class="col-rate numeric">Pay rate</th>
							<th class="col-amount numeric">Pay</th>
							<th class="actions-header" aria-label="Actions"></th>
						</tr>
						<tr v-else>
							<th v-for="column in groupedAppointmentColumns" :key="column.id">
								{{ column.label }}
							</th>
							<th class="col-hours numeric">Hours</th>
							<th v-if="showGroupedSupplierRate" class="col-rate numeric">Pay rate</th>
							<th class="col-amount numeric">Pay</th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="appointmentsIsEmpty">
							<td :colspan="appointmentsColSpan" class="empty">No appointments captured.</td>
						</tr>
						<template v-if="isUngroupedAppointments">
							<tr v-for="item in form.appointmentsSnapshot" :key="item.snapshotId">
								<td>
									<DateRangePicker
										mode="single"
										use-fixed
										animated
										:glass="false"
										:date-from="item.date"
										:date-to="item.date"
										:disabled="isLocked"
										trigger-label="Date"
										placeholder="Date"
										@update="({ dateFrom }) => (item.date = dateFrom)"
									>
								<template #trigger-icon><AppointmentIcon name="calendar" :size="12" /></template>
								<template #previous-icon><AppointmentIcon name="chevron-left" /></template>
								<template #next-icon><AppointmentIcon name="chevron-right" /></template>
									</DateRangePicker>
								</td>
								<td v-tooltip>
									<input v-model="item.buildingName" type="text" aria-label="Building" placeholder="Building" :disabled="isLocked" />
								</td>
								<td :title="engagementLabel(item) || undefined">
									<input v-model="item.supplierName" type="text" aria-label="Cleaner" placeholder="Cleaner" :disabled="isLocked" />
								</td>
								<td v-tooltip>
									<input v-model="item.serviceName" type="text" aria-label="Service" placeholder="Service" :disabled="isLocked" />
								</td>
								<td class="numeric" :title="isSettled(item) ? SETTLED_NOTE : undefined">
									<input
										v-model="item.hours"
										type="number"
										step="0.25"
										aria-label="Hours"
										placeholder="Hours"
										:class="{ 'is-settled': isSettled(item) }"
										:disabled="isLocked || isSettled(item)"
									/>
								</td>
								<td class="numeric" :title="isSettled(item) ? SETTLED_NOTE : undefined">
									<input
										v-model="item.supplierRate"
										type="number"
										step="0.01"
										aria-label="Pay rate"
										placeholder="Rate"
										:class="{ 'is-settled': isSettled(item) }"
										:disabled="isLocked || isSettled(item)"
									/>
								</td>
								<td class="text-value numeric" :title="payNote(item)">{{ formatCurrency(calculatePay(item)) }}</td>
								<td class="actions-cell">
									<RowHoldMenu :disabled="isLocked" @confirm="removeAppointment(item.snapshotId)">Hold to remove row</RowHoldMenu>
								</td>
							</tr>
						</template>
						<template v-else>
							<tr v-for="group in mergedAppointmentsSnapshot" :key="group.groupId">
								<td
									v-for="column in groupedAppointmentColumns"
									:key="column.id"
									class="text-value"
								>
									{{ group.values[column.id] || '—' }}
								</td>
								<td class="text-value numeric">{{ formatNumber(group.hours) }}</td>
								<td v-if="showGroupedSupplierRate" class="text-value numeric">
									{{ formatNumber(group.supplierRate) }}
								</td>
								<td class="text-value numeric">{{ formatCurrency(group.pay) }}</td>
							</tr>
						</template>
					</tbody>
					<tfoot v-if="!appointmentsIsEmpty">
						<tr v-if="isUngroupedAppointments" class="totals-row">
							<td colspan="4" class="totals-label">Total</td>
							<td class="text-value numeric">{{ formatNumber(appointmentTotals.hours) }}</td>
							<td class="text-value numeric totals-muted">—</td>
							<td class="text-value numeric">{{ formatCurrency(appointmentTotals.pay) }}</td>
							<td class="actions-cell"></td>
						</tr>
						<tr v-else class="totals-row">
							<td :colspan="groupedAppointmentColumns.length" class="totals-label">Total</td>
							<td class="text-value numeric">{{ formatNumber(appointmentTotals.hours) }}</td>
							<td v-if="showGroupedSupplierRate" class="text-value numeric totals-muted">—</td>
							<td class="text-value numeric">{{ formatCurrency(appointmentTotals.pay) }}</td>
						</tr>
					</tfoot>
				</table>
			</div>
		</section>

		<section v-if="activeSnapshotTab === 'purchases'" class="snapshot-section">
			<div class="snapshot-toolbar">
				<button class="payment-list-button" type="button" :disabled="isLocked" @click="addPurchase">
					<AppointmentIcon name="plus" />
					Add row
				</button>
			</div>
			<div class="table-wrapper">
				<table class="snapshot-table">
					<thead>
						<tr>
							<th class="col-date">Date</th>
							<th>Building</th>
							<th>COS</th>
							<th class="col-status">Status</th>
							<th class="col-amount numeric">Gross</th>
							<th>Notes</th>
							<th class="actions-header" aria-label="Actions"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!form.purchasesSnapshot.length">
							<td colspan="7" class="empty">No purchases captured.</td>
						</tr>
						<tr v-for="item in form.purchasesSnapshot" :key="item.snapshotId">
							<td>
								<DateRangePicker
									mode="single"
									use-fixed
									animated
									:glass="false"
									:date-from="item.date"
									:date-to="item.date"
									:disabled="isLocked"
									trigger-label="Date"
									placeholder="Date"
									@update="({ dateFrom }) => (item.date = dateFrom)"
								>
								<template #trigger-icon><AppointmentIcon name="calendar" :size="12" /></template>
								<template #previous-icon><AppointmentIcon name="chevron-left" /></template>
								<template #next-icon><AppointmentIcon name="chevron-right" /></template>
								</DateRangePicker>
							</td>
							<td v-tooltip>
								<input v-model="item.buildingName" type="text" aria-label="Building" placeholder="Building" :disabled="isLocked" />
							</td>
							<td v-tooltip>
								<input v-model="item.cosName" type="text" aria-label="COS" placeholder="COS" :disabled="isLocked" />
							</td>
							<td>
								<PurchaseStatusSelect v-model="item.documentStatus" :options="PURCHASE_STATUS_OPTIONS" :disabled="isLocked" aria-label="Purchase status" />
							</td>
							<td class="numeric">
								<input v-model="item.gross" type="number" step="0.01" aria-label="Gross" placeholder="Gross" :disabled="isLocked" />
							</td>
							<td v-tooltip>
								<input v-model="item.notes" type="text" aria-label="Notes" placeholder="Notes" :disabled="isLocked" />
							</td>
							<td class="actions-cell">
								<RowHoldMenu :disabled="isLocked" @confirm="removePurchase(item.snapshotId)">Hold to remove row</RowHoldMenu>
							</td>
						</tr>
					</tbody>
					<tfoot v-if="form.purchasesSnapshot.length">
						<tr class="totals-row">
							<td colspan="4" class="totals-label">Total</td>
							<td class="text-value numeric">{{ formatCurrency(purchaseTotals.gross) }}</td>
							<td></td>
							<td class="actions-cell"></td>
						</tr>
					</tfoot>
				</table>
			</div>
		</section>

		<section v-if="activeSnapshotTab === 'invoices'" class="snapshot-section">
			<div class="snapshot-toolbar">
				<button class="payment-list-button" type="button" :disabled="isLocked" @click="addInvoice">
					<AppointmentIcon name="plus" />
					Add row
				</button>
			</div>
			<div class="table-wrapper">
				<table class="snapshot-table">
					<thead>
						<tr>
							<th class="col-date">Date</th>
							<th class="col-number">Invoice #</th>
							<th>Client</th>
							<th>Building</th>
							<th class="col-amount numeric">Total (inc VAT)</th>
							<th class="col-vat numeric">VAT rate</th>
							<th class="actions-header" aria-label="Actions"></th>
						</tr>
					</thead>
					<tbody>
						<tr v-if="!form.invoicesSnapshot.length">
							<td colspan="7" class="empty">No invoices captured.</td>
						</tr>
						<tr v-for="item in form.invoicesSnapshot" :key="item.snapshotId">
							<td>
								<DateRangePicker
									mode="single"
									use-fixed
									animated
									:glass="false"
									:date-from="item.invoiceDate"
									:date-to="item.invoiceDate"
									:disabled="isLocked"
									trigger-label="Invoice date"
									placeholder="Date"
									@update="({ dateFrom }) => (item.invoiceDate = dateFrom)"
								>
								<template #trigger-icon><AppointmentIcon name="calendar" :size="12" /></template>
								<template #previous-icon><AppointmentIcon name="chevron-left" /></template>
								<template #next-icon><AppointmentIcon name="chevron-right" /></template>
								</DateRangePicker>
							</td>
							<td>
								<input v-model="item.invoiceNumber" type="text" aria-label="Invoice number" placeholder="Invoice #" :disabled="isLocked" />
							</td>
							<td v-tooltip>
								<input v-model="item.clientName" type="text" aria-label="Client" placeholder="Client" :disabled="isLocked" />
							</td>
							<td v-tooltip>
								<input v-model="item.buildingName" type="text" aria-label="Building" placeholder="Building" :disabled="isLocked" />
							</td>
							<td class="numeric">
								<input v-model="item.totalAmount" type="number" step="0.01" aria-label="Total including VAT" placeholder="Total" :disabled="isLocked" />
							</td>
							<td class="numeric">
								<input v-model="item.vatRate" type="number" step="0.01" aria-label="VAT rate" placeholder="VAT %" :disabled="isLocked" />
							</td>
							<td class="actions-cell">
								<RowHoldMenu :disabled="isLocked" @confirm="removeInvoice(item.snapshotId)">Hold to remove row</RowHoldMenu>
							</td>
						</tr>
					</tbody>
					<tfoot v-if="form.invoicesSnapshot.length">
						<tr class="totals-row">
							<td colspan="4" class="totals-label">Total</td>
							<td class="text-value numeric">{{ formatCurrency(invoiceTotals.totalAmount) }}</td>
							<td></td>
							<td class="actions-cell"></td>
						</tr>
					</tfoot>
				</table>
			</div>
		</section>

	</div>
</template>

	<script setup>
	import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
	import Big from 'big.js';
	import AnalyticsChartCard from '@/components/analytics/AnalyticsChartCard.vue';
	import CatalogSearchInput from '@/components/CatalogSearchInput.vue';
	import DateRangePicker from '@/components/DateRangePicker.vue';
	import AppointmentIcon from '@/components/icons/AppointmentIcon.vue';
	import RowHoldMenu from '@/components/common/RowHoldMenu.vue';
	import SegmentedSwitch from '@/components/common/SegmentedSwitch.vue';
	import PurchaseStatusSelect from '@/components/journal/PurchaseStatusSelect.vue';
	import '@/assets/styles/payment-lists.css';
	import { useAnalyticsReportsStore } from '@/stores/analyticsReports';
	import { useTabsStore } from '@/stores/tabs';
	import {
		buildSummaryFromSnapshots,
		calculateAppointmentPay,
		calculatePurchaseGrossImpact,
	} from '@/utils/analyticsSnapshot';

const props = defineProps({
	reportId: {
		type: [String, Number],
		default: null,
	},
	draftId: {
		type: [String, Number],
		default: null,
	},
	tabId: {
		type: String,
		default: null,
	},
});

const PURCHASE_STATUS_OPTIONS = ['', '✓', '✕', 'PE', 'DR', 'Refund', '?'];

const reportsStore = useAnalyticsReportsStore();
const tabsStore = useTabsStore();
const { proxy } = getCurrentInstance();
const toast = proxy?.$toast;
const logError = proxy?.$logError;

const reportId = ref(props.reportId ? String(props.reportId) : null);
const isSaving = ref(false);
const isRefreshingSnapshot = ref(false);
const isHydratingReport = ref(false);
const isAutoSaveQueued = ref(false);
let snapshotRequestSequence = 0;
let autoSaveTimerId = null;
const AUTO_SAVE_DELAY_MS = 900;

const form = reactive({
	name: '',
	dateFrom: '',
	dateTo: '',
	status: 'draft',
	notes: '',
	appointmentsSnapshot: [],
	purchasesSnapshot: [],
	invoicesSnapshot: [],
	wagePayments: [],
});

const summary = computed(() =>
	buildSummaryFromSnapshots({
		appointments: form.appointmentsSnapshot,
		purchases: form.purchasesSnapshot,
		invoices: form.invoicesSnapshot,
		wagePayments: form.wagePayments,
	}),
);

const toNumber = (value, fallback = 0) => {
	const numeric = Number(value);
	return Number.isFinite(numeric) ? numeric : fallback;
};

const formatCompactCurrency = (value) =>
	new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
		notation: 'compact',
		maximumFractionDigits: 1,
	}).format(toNumber(value, 0));

const normalizeVatRate = (value) => {
	const numeric = toNumber(value, NaN);
	if (!Number.isFinite(numeric) || numeric < 0) return null;
	if (numeric > 1) return numeric / 100;
	return numeric;
};

const resolveInvoiceRevenueExVat = (item = {}) => {
	const subtotal = toNumber(
		item?.subtotalAmount ??
		item?.invoiceContent?.summary?.subtotal,
		NaN,
	);
	if (Number.isFinite(subtotal)) return subtotal;

	const total = toNumber(
		item?.totalAmount ??
		item?.invoiceContent?.summary?.total,
		NaN,
	);
	if (!Number.isFinite(total)) return 0;

	const vat = toNumber(
		item?.vatAmount ??
		item?.invoiceContent?.summary?.vat,
		NaN,
	);
	if (Number.isFinite(vat)) return total - vat;

	const vatRate = normalizeVatRate(item?.vatRate ?? item?.invoiceContent?.vatRate);
	if (vatRate !== null && vatRate > 0) {
		return total / (1 + vatRate);
	}
	return total;
};

const resolveInvoiceBuildingRevenueBreakdown = (invoice = {}) => {
	const source = Array.isArray(invoice?.buildingBreakdown) ? invoice.buildingBreakdown : [];
	const normalized = source
		.map((entry) => ({
			buildingName: String(entry?.buildingName || '').trim() || 'Unassigned',
			amount: toNumber(entry?.amount, NaN),
		}))
		.filter((entry) => Number.isFinite(entry.amount) && Math.abs(entry.amount) > 0.005);

	if (!normalized.length) return [];

	const invoiceRevenue = toNumber(resolveInvoiceRevenueExVat(invoice), 0);
	const breakdownTotal = normalized.reduce((sum, entry) => sum + entry.amount, 0);
	if (Math.abs(breakdownTotal) <= 0.005) return [];

	const scale = invoiceRevenue / breakdownTotal;
	return normalized.map((entry) => ({
		buildingName: entry.buildingName,
		amount: entry.amount * scale,
	}));
};

const costStructureRows = computed(() => {
	const labor = Math.max(0, toNumber(summary.value?.laborCost));
	const purchases = Math.max(0, toNumber(summary.value?.purchasesGross));
	const margin = toNumber(summary.value?.grossMargin);
	const rows = [
		{ name: 'Cleaner pay', value: labor },
		{ name: 'Purchases', value: purchases },
		{ name: margin >= 0 ? 'Gross margin' : 'Loss', value: Math.abs(margin) },
	];
	return rows.filter((entry) => entry.value > 0.005);
});

const hasCostStructureData = computed(() => costStructureRows.value.length > 0);

const costStructureOption = computed(() => ({
	tooltip: { trigger: 'item' },
	legend: {
		bottom: 0,
		textStyle: { color: 'var(--text-color-secondary, #5e6170)', fontSize: 11 },
	},
	series: [
		{
			type: 'pie',
			radius: ['44%', '70%'],
			center: ['50%', '46%'],
			avoidLabelOverlap: true,
			itemStyle: { borderColor: '#fff', borderWidth: 2 },
			label: { show: false },
			emphasis: { label: { show: true, fontSize: 12, fontWeight: 600 } },
			data: costStructureRows.value,
			color: ['#2F6FED', '#7A8796', '#27A376'],
		},
	],
}));

const buildingProfitabilityRows = computed(() => {
	const grouped = new Map();
	const getRow = (rawBuildingName) => {
		const buildingName = String(rawBuildingName || '').trim() || 'Unassigned';
		if (!grouped.has(buildingName)) {
			grouped.set(buildingName, {
				buildingName,
				revenue: 0,
				labor: 0,
				purchases: 0,
			});
		}
		return grouped.get(buildingName);
	};

	(form.invoicesSnapshot || []).forEach((invoice) => {
		const breakdown = resolveInvoiceBuildingRevenueBreakdown(invoice);
		if (breakdown.length) {
			breakdown.forEach((entry) => {
				const row = getRow(entry.buildingName);
				row.revenue += toNumber(entry.amount);
			});
			return;
		}
		const row = getRow(invoice?.buildingName);
		row.revenue += toNumber(resolveInvoiceRevenueExVat(invoice));
	});

	(form.appointmentsSnapshot || []).forEach((appointment) => {
		const row = getRow(appointment?.buildingName);
		row.labor += toNumber(calculateAppointmentPay(appointment));
	});

	(form.purchasesSnapshot || []).forEach((purchase) => {
		const row = getRow(purchase?.buildingName);
		row.purchases += calculatePurchaseGrossImpact(purchase);
	});

	const rows = Array.from(grouped.values())
		.map((entry) => {
			const cost = entry.labor + entry.purchases;
			return {
				...entry,
				cost,
				margin: entry.revenue - cost,
			};
		})
		.filter((entry) => Math.abs(entry.revenue) + Math.abs(entry.cost) + Math.abs(entry.margin) > 0.005);

	rows.sort((a, b) => b.revenue - a.revenue);
	return rows.slice(0, 12);
});

const hasBuildingProfitabilityData = computed(() => buildingProfitabilityRows.value.length > 0);

const buildingProfitabilityOption = computed(() => {
	const rows = [...buildingProfitabilityRows.value].reverse();
	return {
		tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
		legend: {
			top: 0,
			textStyle: { color: 'var(--text-color-secondary, #5e6170)', fontSize: 11 },
		},
		grid: { left: 124, right: 14, top: 36, bottom: 14, containLabel: false },
		xAxis: {
			type: 'value',
			axisLabel: {
				color: 'var(--text-color-secondary, #5e6170)',
				fontSize: 11,
				formatter: (value) => formatCompactCurrency(value),
			},
			splitLine: { lineStyle: { color: 'rgba(0, 0, 0, 0.08)' } },
		},
		yAxis: {
			type: 'category',
			data: rows.map((entry) => entry.buildingName),
			axisLabel: {
				color: 'var(--text-color-secondary, #5e6170)',
				fontSize: 11,
				width: 112,
				overflow: 'truncate',
			},
		},
		series: [
			{
				name: 'Revenue',
				type: 'bar',
				data: rows.map((entry) => Number(entry.revenue.toFixed(2))),
				barMaxWidth: 20,
				itemStyle: { color: '#2F6FED' },
			},
			{
				name: 'Cost',
				type: 'bar',
				data: rows.map((entry) => Number(entry.cost.toFixed(2))),
				barMaxWidth: 20,
				itemStyle: { color: '#7A8796' },
			},
			{
				name: 'Margin',
				type: 'bar',
				data: rows.map((entry) => ({
					value: Number(entry.margin.toFixed(2)),
					itemStyle: {
						color: entry.margin < 0 ? '#C45151' : '#27A376',
					},
				})),
				barMaxWidth: 20,
				itemStyle: { color: '#27A376' },
			},
		],
	};
});

	const isLocked = computed(() => form.status === 'locked');
	const headerTitle = computed(() => 'Analytics');
	const groupBySelection = ref([]);
	const activeSnapshotTab = ref('appointments');
	// Where a row's pay comes from, shown on hover rather than as extra text in the row.
	const SETTLED_NOTE = 'Set by a saved pay calculation';
	const isSettled = (item) => item?.costSource === 'settlement';
	const payNote = (item) => {
		if (isSettled(item)) return 'From a saved pay calculation';
		if (item?.costSource === 'estimate') return 'Estimate: hours × pay rate';
		return undefined;
	};
	const engagementLabel = (item) => ({ employee: 'Employee', self_employed: 'Subcontractor' })[item?.engagementType] || '';
	const snapshotTabOptions = [
		{ value: 'appointments', label: 'Appointments' },
		{ value: 'invoices', label: 'Client invoices' },
		{ value: 'purchases', label: 'Purchases' },
	];

	const toIsoDate = (date) => {
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, '0');
		const day = String(date.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	const monthRangeFromIso = (value) => {
		if (!value) return null;
		const [yearRaw, monthRaw] = String(value).split('-');
		const year = Number(yearRaw);
		const month = Number(monthRaw);
		if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) {
			return null;
		}
		const start = new Date(year, month - 1, 1);
		const end = new Date(year, month, 0);
		return {
			dateFrom: toIsoDate(start),
			dateTo: toIsoDate(end),
		};
	};

	const handleMonthPickerUpdate = ({ dateFrom, dateTo }) => {
		if (isLocked.value) return;
		const anchor = dateFrom || dateTo || '';
		if (!anchor) {
			form.dateFrom = '';
			form.dateTo = '';
			return;
		}
		const monthRange = monthRangeFromIso(anchor);
		if (!monthRange) return;
		if (form.dateFrom === monthRange.dateFrom && form.dateTo === monthRange.dateTo) return;
		form.dateFrom = monthRange.dateFrom;
		form.dateTo = monthRange.dateTo;
	};

	const GROUP_BY_ORDER = ['building', 'cleaner', 'service'];
	const GROUP_BY_LABELS = {
		building: 'Building',
		cleaner: 'Cleaner',
		service: 'Service',
	};

	const groupByOptions = GROUP_BY_ORDER.map((field) => ({
		id: field,
		name: GROUP_BY_LABELS[field],
	}));

	const groupByFields = computed(() =>
		GROUP_BY_ORDER.filter((field) => groupBySelection.value.includes(field)),
	);
	const groupedAppointmentColumns = computed(() =>
		groupByFields.value.map((field) => ({
			id: field,
			label: GROUP_BY_LABELS[field],
		})),
	);
	const isUngroupedAppointments = computed(() => groupByFields.value.length === 0);

	const updateGroupBySelection = (nextSelection = []) => {
		const normalized = Array.isArray(nextSelection)
			? nextSelection.map((value) => String(value))
			: [];
		groupBySelection.value = GROUP_BY_ORDER.filter((field) => normalized.includes(field));
	};

	const normalizeKeyPart = (value) => String(value || '').trim().toLowerCase();
	const normalizeKeyValue = (idValue, nameValue, prefix) => {
		if (idValue !== null && idValue !== undefined && idValue !== '') {
			return `${prefix}:id:${String(idValue)}`;
		}
		const name = normalizeKeyPart(nameValue);
		return `${prefix}:name:${name || '—'}`;
	};

	const buildAppointmentGroupKey = (appointment, fields = []) => {
		const parts = [];
		if (fields.includes('building')) {
			parts.push(
				normalizeKeyValue(appointment?.buildingId, appointment?.buildingName, 'building'),
			);
		}
		if (fields.includes('cleaner')) {
			parts.push(
				normalizeKeyValue(appointment?.supplierId, appointment?.supplierName, 'cleaner'),
			);
		}
		if (fields.includes('service')) {
			parts.push(
				normalizeKeyValue(appointment?.serviceId, appointment?.serviceName, 'service'),
			);
		}
		return parts.join('|');
	};

	const resolveGroupValue = (field, appointment) => {
		if (field === 'building') {
			return appointment?.buildingName || '—';
		}
		if (field === 'cleaner') {
			return appointment?.supplierName || '—';
		}
		if (field === 'service') {
			return appointment?.serviceName || '—';
		}
		return '—';
	};

	const toBigSafe = (value) => {
		if (value === null || value === undefined || value === '') return new Big(0);
		const raw = String(value).replace(/,/g, '').trim();
		if (!raw) return new Big(0);
		try {
			return new Big(raw);
		} catch {
			return new Big(0);
		}
	};

	const sumBigBy = (rows = [], selector) =>
		(Array.isArray(rows) ? rows : []).reduce(
			(sum, row) => sum.plus(toBigSafe(selector(row))),
			new Big(0),
		);

	const resolveInvoiceTotalIncVat = (item = {}) =>
		item?.totalAmount ??
		item?.invoiceContent?.summary?.total ??
		item?.summary?.total ??
		item?.subtotalAmount ??
		0;

	const appointmentTotals = computed(() => ({
		hours: summary.value.totalHours,
		pay: summary.value.laborCost,
	}));

	const purchaseTotals = computed(() => ({
		gross: summary.value.purchasesGross,
	}));

	const invoiceTotals = computed(() => ({
		totalAmount: Number(
			sumBigBy(form.invoicesSnapshot, resolveInvoiceTotalIncVat).toFixed(2),
		),
	}));

	const mergedAppointmentsSnapshot = computed(() => {
		const activeFields = groupByFields.value;
		if (!activeFields.length) {
			return [];
		}
		const grouped = new Map();

		(form.appointmentsSnapshot || []).forEach((appointment) => {
			const key = buildAppointmentGroupKey(appointment, activeFields);
			if (!key) return;
			const values = {};
			activeFields.forEach((field) => {
				values[field] = resolveGroupValue(field, appointment);
			});
			const hours = toBigSafe(appointment?.hours);
			const pay = toBigSafe(calculateAppointmentPay(appointment));

			const rawRate = appointment?.supplierRate;
			const numericRate = rawRate === '' || rawRate === null || rawRate === undefined ? null : Number(rawRate);
			const rateIsValid = numericRate !== null && Number.isFinite(numericRate);

			if (!grouped.has(key)) {
				grouped.set(key, {
					groupId: key,
					values,
					hours,
					pay,
					rate: rateIsValid ? numericRate : null,
					rateIsMixed: false,
				});
				return;
			}

			const existing = grouped.get(key);
			activeFields.forEach((field) => {
				if (!existing.values[field] || existing.values[field] === '—') {
					existing.values[field] = values[field];
				}
			});
			existing.hours = existing.hours.plus(hours);
			existing.pay = existing.pay.plus(pay);
			if (rateIsValid) {
				if (existing.rate === null) {
					existing.rate = numericRate;
				} else if (Math.abs(existing.rate - numericRate) > 0.0001) {
					existing.rateIsMixed = true;
				}
			}
		});

		const rows = Array.from(grouped.values()).map((entry) => ({
			groupId: entry.groupId,
			values: entry.values || {},
			hours: Number(entry.hours.toFixed(2)),
			supplierRate: entry.rateIsMixed ? null : entry.rate,
			pay: Number(entry.pay.toFixed(2)),
		}));

		rows.sort((a, b) => {
			for (const field of activeFields) {
				const aValue = a.values[field] || '';
				const bValue = b.values[field] || '';
				const compare = aValue.localeCompare(bValue);
				if (compare) return compare;
			}
			return 0;
		});

		return rows;
	});

	const appointmentsColSpan = computed(() => {
		if (isUngroupedAppointments.value) return 8;
		return groupedAppointmentColumns.value.length + (showGroupedSupplierRate.value ? 3 : 2);
	});

	const showGroupedSupplierRate = computed(() => {
		if (isUngroupedAppointments.value) return true;
		const active = groupByFields.value;
		const isBuildingOnly = active.length === 1 && active[0] === 'building';
		const isCleanerOnly = active.length === 1 && active[0] === 'cleaner';
		const isBuildingCleanerOnly =
			active.length === 2 && active.includes('building') && active.includes('cleaner');
		return !(isBuildingOnly || isCleanerOnly || isBuildingCleanerOnly);
	});
	const appointmentsIsEmpty = computed(() => {
		if (isUngroupedAppointments.value) {
			return !form.appointmentsSnapshot.length;
		}
		return !mergedAppointmentsSnapshot.value.length;
	});

const ensureReportName = () => {
	if (form.name?.trim()) return;
	form.name = 'Analytics';
};

const cloneSnapshotRows = (rows = []) =>
	Array.isArray(rows) ? rows.map((item) => ({ ...(item || {}) })) : [];

const hasValue = (value) => value !== null && value !== undefined && value !== '';

const normalizeInvoiceSnapshotRows = (rows = []) =>
	(Array.isArray(rows) ? rows : []).map((item) => {
		const next = { ...(item || {}) };
		const hasSubtotal = hasValue(next.subtotalAmount);
		const hasVatAmount = hasValue(next.vatAmount);
		const hasVatRate = hasValue(next.vatRate);
		const hasTotal = hasValue(next.totalAmount);
		if (!hasSubtotal && !hasVatAmount && !hasVatRate && hasTotal) {
			next.vatRate = 0.2;
		}
		return next;
	});

const setFormFromReport = (report) => {
	form.name = report?.name || '';
	form.dateFrom = report?.dateFrom || '';
	form.dateTo = report?.dateTo || '';
	form.status = report?.status || 'draft';
	form.notes = report?.notes || '';
	form.appointmentsSnapshot = cloneSnapshotRows(report?.appointmentsSnapshot || []);
	form.purchasesSnapshot = cloneSnapshotRows(report?.purchasesSnapshot || []);
	form.invoicesSnapshot = normalizeInvoiceSnapshotRows(report?.invoicesSnapshot || []);
	form.wagePayments = cloneSnapshotRows(report?.wagePayments || []);
};

const refetchSnapshotForDateRange = async () => {
	if (isLocked.value) return;
	if (!form.dateFrom || !form.dateTo) return;
	const requestId = ++snapshotRequestSequence;
	try {
		isRefreshingSnapshot.value = true;
		ensureReportName();
		const snapshot = await reportsStore.createSnapshot({
			dateFrom: form.dateFrom,
			dateTo: form.dateTo,
		});
		if (requestId !== snapshotRequestSequence) return;
		form.appointmentsSnapshot = cloneSnapshotRows(snapshot?.appointmentsSnapshot || []);
		form.purchasesSnapshot = cloneSnapshotRows(snapshot?.purchasesSnapshot || []);
		form.invoicesSnapshot = normalizeInvoiceSnapshotRows(snapshot?.invoicesSnapshot || []);
		form.wagePayments = cloneSnapshotRows(snapshot?.wagePayments || []);
	} catch (err) {
		logError(err, 'AnalyticsEditor refetchSnapshotForDateRange');
		toast?.error?.('Failed to refresh data for selected date range.');
	} finally {
		if (requestId === snapshotRequestSequence) {
			isRefreshingSnapshot.value = false;
		}
	}
};

const buildSavePayload = () => ({
	name: form.name,
	dateFrom: form.dateFrom,
	dateTo: form.dateTo,
	status: form.status || 'draft',
	notes: form.notes,
	appointmentsSnapshot: form.appointmentsSnapshot,
	purchasesSnapshot: form.purchasesSnapshot,
	invoicesSnapshot: form.invoicesSnapshot,
	wagePayments: form.wagePayments,
	summary: summary.value,
});

const clearAutoSaveTimer = () => {
	if (!autoSaveTimerId) return;
	clearTimeout(autoSaveTimerId);
	autoSaveTimerId = null;
};

const persistReport = async () => {
	if (!form.dateFrom || !form.dateTo) return;
	if (isLocked.value && reportId.value) return;
	if (isSaving.value || isRefreshingSnapshot.value || isHydratingReport.value) {
		isAutoSaveQueued.value = true;
		return;
	}
	try {
		isSaving.value = true;
		ensureReportName();
		const payload = buildSavePayload();
		if (reportId.value) {
			await reportsStore.updateReport(reportId.value, payload);
		} else {
			const created = await reportsStore.createReport(payload);
			reportId.value = String(created.documentId || created.id);
			if (props.tabId) {
				tabsStore.updateTab(props.tabId, {
					itemId: reportId.value,
					title: headerTitle.value,
					props: { reportId: reportId.value },
				});
			}
		}
	} catch (err) {
		logError(err, 'AnalyticsEditor persistReport');
		toast?.error?.('Failed to auto-save analytics report.');
	} finally {
		isSaving.value = false;
	}
};

const queueAutoSave = (delayMs = AUTO_SAVE_DELAY_MS) => {
	isAutoSaveQueued.value = true;
	clearAutoSaveTimer();
	autoSaveTimerId = setTimeout(async () => {
		autoSaveTimerId = null;
		if (!isAutoSaveQueued.value) return;
		isAutoSaveQueued.value = false;
		await persistReport();
		if (isAutoSaveQueued.value) {
			queueAutoSave(200);
		}
	}, delayMs);
};

	const addAppointment = () => {
		form.appointmentsSnapshot.push({
			snapshotId: `appt-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
			date: '',
		buildingName: '',
		supplierName: '',
		serviceName: '',
		hours: '',
			supplierRate: '',
		});
	};

	const handleAddAppointment = () => {
		if (isLocked.value) return;
		updateGroupBySelection([]);
		addAppointment();
	};

	const removeAppointment = (id) => {
		form.appointmentsSnapshot = form.appointmentsSnapshot.filter((item) => item.snapshotId !== id);
	};

const addPurchase = () => {
	form.purchasesSnapshot.push({
		snapshotId: `purchase-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
		date: '',
		buildingName: '',
		cosName: '',
		documentStatus: '',
		gross: '',
		notes: '',
	});
};

const removePurchase = (id) => {
	form.purchasesSnapshot = form.purchasesSnapshot.filter((item) => item.snapshotId !== id);
};

const addInvoice = () => {
	form.invoicesSnapshot.push({
		snapshotId: `invoice-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
		invoiceDate: '',
		invoiceNumber: '',
		clientName: '',
		buildingName: '',
		totalAmount: '',
		vatRate: 0.2,
	});
};

const removeInvoice = (id) => {
	form.invoicesSnapshot = form.invoicesSnapshot.filter((item) => item.snapshotId !== id);
};

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '-';
	return new Intl.NumberFormat('en-GB', {
		style: 'currency',
		currency: 'GBP',
	}).format(numeric);
};

const formatPercent = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '-';
	return `${numeric.toFixed(2)}%`;
};

const formatNumber = (value) => {
	if (value === null || value === undefined || value === '') return '-';
	const numeric = Number(value);
	if (!Number.isFinite(numeric)) return '-';
	return numeric.toFixed(2);
};

const calculatePay = (item) => calculateAppointmentPay(item);

watch(
	() => [form.dateFrom, form.dateTo],
	async ([nextFrom, nextTo], [prevFrom, prevTo]) => {
		if (isHydratingReport.value) return;
		if (nextFrom === prevFrom && nextTo === prevTo) return;
		if (!nextFrom || !nextTo) return;
		await refetchSnapshotForDateRange();
	},
);

watch(
	() => ({
		name: form.name,
		dateFrom: form.dateFrom,
		dateTo: form.dateTo,
		notes: form.notes,
		appointmentsSnapshot: form.appointmentsSnapshot,
		purchasesSnapshot: form.purchasesSnapshot,
		invoicesSnapshot: form.invoicesSnapshot,
	wagePayments: form.wagePayments,
	}),
	() => {
		if (isHydratingReport.value) return;
		queueAutoSave();
	},
	{ deep: true },
);

watch(
	() => isRefreshingSnapshot.value,
	(isRefreshing) => {
		if (!isRefreshing && isAutoSaveQueued.value) {
			queueAutoSave(120);
		}
	},
);

watch(
	() => isSaving.value,
	(savingNow) => {
		if (!savingNow && isAutoSaveQueued.value) {
			queueAutoSave(120);
		}
	},
);

onMounted(async () => {
	if (!reportsStore.reports.length && !reportsStore.isLoading) {
		try {
			await reportsStore.fetchReports({ page: 1 });
		} catch (err) {
			logError(err, 'AnalyticsEditor preloadReports');
		}
	}
	if (!reportId.value) {
		form.status = 'draft';
		return;
	}
	try {
		const report = await reportsStore.fetchReportById(reportId.value);
		isHydratingReport.value = true;
		setFormFromReport(report);
		await nextTick();
		isHydratingReport.value = false;
		if (props.tabId) {
			tabsStore.updateTab(props.tabId, { title: headerTitle.value });
		}
	} catch (err) {
		isHydratingReport.value = false;
		logError(err, 'AnalyticsEditor loadReport');
	}
});

onBeforeUnmount(() => {
	clearAutoSaveTimer();
});
</script>

<style scoped>
/* The report follows the journal pages: their type scale, flat bordered tiles and journal tables. */
.analytics-editor {
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 24px;
	background: var(--noc-background);
	color: var(--text-color);
	font: var(--ap-type-body);
}

.editor-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 16px;
}

.editor-header h2 {
	margin: 0;
	color: var(--text-color);
	font: var(--ap-type-title);
}

.header-actions {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
}

/* The month picker matches the journal date controls. */
.analytics-month-picker :deep(.drp-trigger) {
	width: 260px;
	height: 36px;
	padding: 0 12px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
	box-shadow: none;
	color: var(--text-color);
	font: var(--ap-type-control);
	font-variant-numeric: tabular-nums;
}

.analytics-month-picker :deep(.drp-trigger:hover:not(:disabled)) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.analytics-month-picker :deep(.drp-trigger-text) {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.range-status {
	margin: 0;
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

/* Key figures sit in a flat grid of bordered tiles. */
.summary-grid {
	display: grid;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 12px;
}

.summary-card {
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding: 12px 16px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
}

.summary-label,
.summary-card small {
	color: var(--text-color-secondary);
	font: var(--ap-type-caption);
}

.summary-value {
	color: var(--text-color);
	font: var(--ap-type-total);
	font-variant-numeric: tabular-nums;
}

.summary-value.negative {
	color: var(--noc-red, #c45151);
}

.charts-stack {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.charts-overview-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.snapshot-tabs-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	gap: 12px;
}

.snapshot-section {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

/* "Add row" leads on every tab, like "Add purchase" in the journal; grouping sits beside it. */
.snapshot-toolbar {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 12px;
}

.group-by {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
}

.group-by-label {
	color: var(--text-color-secondary);
	font: var(--ap-type-control);
	white-space: nowrap;
}

.group-by-input {
	width: 300px;
	max-width: 100%;
}

.group-by-input.catalog-search-input :deep(.field-input) {
	min-height: 34px;
	padding: 4px 10px;
	border: 1px solid var(--border-color);
	border-radius: 8px;
	background: var(--noc-background);
	font: var(--ap-type-body);
	box-sizing: border-box;
}

.group-by-input.catalog-search-input :deep(.field-input.multi-input) {
	align-items: center;
	gap: 4px;
}

.group-by-input.catalog-search-input :deep(.multi-input-field) {
	min-height: 0;
	margin: 0;
	padding: 0;
	font: var(--ap-type-body);
}

.group-by-input.catalog-search-input :deep(.multi-chip) {
	padding: 1px 8px;
	border-radius: 6px;
	font: var(--ap-type-label);
}

.group-by-input.catalog-search-input :deep(.field-input:focus-within) {
	border-color: var(--noc-blue);
}

/* Snapshot tables look like the Purchases journal: 40px rows, bordered cells, controls filling them. */
.table-wrapper {
	overflow-x: auto;
	border-inline: 1px solid var(--border-color);
	background: var(--noc-background);
}

.snapshot-table {
	width: 100%;
	min-width: 860px;
	border-collapse: separate;
	border-spacing: 0;
	table-layout: fixed;
	color: var(--text-color);
	font: var(--ap-type-body);
}

.snapshot-table .col-date {
	width: 150px;
}

.snapshot-table .col-number {
	width: 130px;
}

.snapshot-table .col-status {
	width: 110px;
}

.snapshot-table .col-hours,
.snapshot-table .col-vat {
	width: 100px;
}

.snapshot-table .col-rate {
	width: 110px;
}

.snapshot-table .col-amount {
	width: 140px;
}

.snapshot-table .actions-header {
	width: 56px;
}

.snapshot-table th {
	height: 38px;
	padding: 10px 16px;
	box-sizing: border-box;
	border-top: 1px solid var(--border-color);
	border-bottom: 1px solid var(--border-color);
	background: #fafafa;
	color: var(--text-color);
	font: var(--ap-type-table-label);
	font-weight: 600;
	text-align: left;
	vertical-align: middle;
	white-space: nowrap;
}

.snapshot-table td {
	height: 40px;
	padding: 0;
	border-bottom: 1px solid var(--border-color);
	vertical-align: middle;
}

.snapshot-table td:not(:last-child) {
	border-right: 1px solid var(--border-color);
}

.snapshot-table .text-value {
	padding: 10px 16px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.snapshot-table .numeric {
	text-align: right;
	font-variant-numeric: tabular-nums;
}

.snapshot-table input {
	display: block;
	width: 100%;
	height: 40px;
	padding: 10px 16px;
	box-sizing: border-box;
	overflow: hidden;
	border: 0;
	border-radius: 0;
	background: transparent;
	color: var(--text-color);
	font: var(--ap-type-body);
	text-overflow: ellipsis;
	outline: none;
	transition: background-color 0.1s ease;
}

.snapshot-table .numeric input {
	text-align: right;
	font-variant-numeric: tabular-nums;
}

.snapshot-table input::placeholder {
	color: var(--text-color-secondary);
	opacity: 0.4;
}

.snapshot-table input:hover:not(:disabled),
.snapshot-table input:focus {
	background-color: var(--noc-background-secondary, #fafafa);
}

/* A locked report reads normally; only values set by a saved pay calculation are dimmed. */
.snapshot-table input:disabled {
	opacity: 1;
	color: var(--text-color);
	cursor: default;
}

.snapshot-table input.is-settled {
	color: var(--text-color-secondary);
}

.snapshot-table input[type='number']::-webkit-outer-spin-button,
.snapshot-table input[type='number']::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

.snapshot-table input[type='number'] {
	-moz-appearance: textfield;
	appearance: textfield;
}

/* The date opens the journal calendar, its trigger filling the cell like in Purchases. */
.snapshot-table :deep(.date-range-picker) {
	width: 100%;
	min-width: 0;
}

.snapshot-table :deep(.drp-trigger) {
	width: 100%;
	height: 40px;
	min-height: 40px;
	box-sizing: border-box;
	flex-direction: row-reverse;
	justify-content: space-between;
	padding: 10px 16px;
	border: 0;
	border-radius: 0;
	box-shadow: none;
	background: transparent;
	color: var(--text-color);
	font: var(--ap-type-body);
	font-variant-numeric: tabular-nums;
}

.snapshot-table :deep(.drp-trigger:hover:not(:disabled)),
.snapshot-table :deep(.drp-trigger[aria-expanded='true']) {
	background: var(--noc-background-secondary, #fafafa);
}

.snapshot-table :deep(.drp-trigger:focus-visible) {
	outline: 1px solid var(--noc-blue);
	outline-offset: -1px;
}

.snapshot-table :deep(.drp-trigger:disabled) {
	opacity: 1;
	cursor: default;
}

.snapshot-table :deep(.drp-trigger-icon),
.snapshot-table :deep(.drp-trigger .appointment-icon) {
	color: var(--text-color-secondary);
}

.snapshot-table :deep(.drp-trigger-text) {
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.snapshot-table tfoot td {
	background: #fafafa;
	font: var(--ap-type-control);
	font-weight: 600;
}

.snapshot-table .totals-label {
	padding: 10px 16px;
}

.snapshot-table .totals-muted {
	color: var(--text-color-secondary);
	font-weight: 500;
}

.snapshot-table .actions-cell {
	padding: 0 8px;
	text-align: center;
}

.snapshot-table .empty {
	padding: 24px 16px;
	color: var(--text-color-secondary);
	text-align: center;
}

/* The calendars share the journal type scale and hover tint. */
.analytics-editor :deep(.drp-dropdown) { border-radius: 8px; }
.analytics-editor :deep(.drp-btn) { border-radius: 8px; min-height: 34px; }
.analytics-editor :deep(.drp-month-title) { font: var(--ap-type-section); color: var(--text-color); }
.analytics-editor :deep(.drp-month-cell),
.analytics-editor :deep(.drp-btn) { font: var(--ap-type-control); }
.analytics-editor :deep(.drp-cell-num),
.analytics-editor :deep(.drp-preset-btn) { font: var(--ap-type-body); }
.analytics-editor :deep(.drp-weekday) { font: var(--ap-type-label); }
.analytics-editor :deep(.drp-footer-range) { font: var(--ap-type-caption); }
.analytics-editor :deep(.drp-cell-num),
.analytics-editor :deep(.drp-footer-range) { font-variant-numeric: tabular-nums; }
.analytics-editor :deep(.drp-nav-btn:hover:not(:disabled)),
.analytics-editor :deep(.drp-month-title--clickable:hover:not(:disabled)),
.analytics-editor :deep(.drp-preset-btn:not(.drp-preset-btn--active):hover:not(:disabled)),
.analytics-editor :deep(.drp-month-cell:not(.drp-month-cell--selected):hover:not(:disabled)),
.analytics-editor :deep(.drp-btn--cancel:hover:not(:disabled)),
.analytics-editor :deep(.drp-cell:not(.drp-cell--empty):not(.drp-cell--selected):hover:not(:disabled) .drp-cell-num) {
	background-color: color-mix(in srgb, var(--text-color-secondary) 4%, transparent);
}

.analytics-editor :deep(.drp-btn--update:hover:not(:disabled)),
.analytics-editor :deep(.drp-month-cell--selected:hover:not(:disabled)),
.analytics-editor :deep(.drp-cell--selected:hover:not(:disabled) .drp-cell-num) {
	background-color: var(--primary-hover);
	color: var(--noc-white);
}

.analytics-editor :deep(.drp-cell--selected .drp-cell-num),
.analytics-editor :deep(.drp-month-cell--selected),
.analytics-editor :deep(.drp-preset-btn--active) {
	font-weight: 500;
}

.analytics-editor :deep(.drp-cell--today:not(.drp-cell--selected) .drp-cell-num) {
	font-weight: 700;
}

@media (max-width: 1140px) {
	.charts-overview-grid {
		grid-template-columns: 1fr;
	}
}

@media (max-width: 760px) {
	.analytics-editor {
		padding: 16px;
	}

	.group-by,
	.group-by-input {
		width: 100%;
	}
}
</style>
